/**
 * useAutoSaveRevision — pushes SLD edits to the backend revision
 * after a debounce window. Only active when the editor is in
 * project mode (revisionId !== null). Standalone sessions skip
 * the API entirely and rely on localStorage as before.
 *
 * Design notes:
 *   - Watches topology / nodes / edges for changes. Any change
 *     resets the debounce timer; we only fire one save per quiet
 *     window so dragging a symbol around doesn't generate a
 *     hundred POSTs.
 *   - The debounce is intentionally generous (~2.5s). Most edits
 *     come in bursts (drag, type, click) and we want them coalesced.
 *   - Right after the editor LOADS a revision (project handoff or
 *     manual switch), we naturally re-render with the same
 *     topology — that doesn't need to be saved back. The save
 *     attempt would be harmless (same payload, no-op on backend)
 *     but generates noise. We track a "savedRevisionRef" so we
 *     skip the save right after a revision change.
 *   - Errors get logged + reflected in saveStatus so the toolbar
 *     can show "Save failed". We don't throw or notify — better
 *     to keep the editor working than block on a transient outage.
 */
import { useEffect, useRef } from 'react';
import { useEditorStore } from '../store/editor';
import { updateRevision } from '../api/revisions';

const AUTOSAVE_DEBOUNCE_MS = 2500;

export function useAutoSaveRevision(): void {
  const projectId  = useEditorStore((s) => s.projectId);
  const revisionId = useEditorStore((s) => s.revisionId);
  const topology   = useEditorStore((s) => s.topology);
  const nodes      = useEditorStore((s) => s.nodes);
  const edges      = useEditorStore((s) => s.edges);

  const timerRef = useRef<number | null>(null);
  // Tracks which revision id was current when we last "settled" —
  // i.e. just after a load. We only auto-save once we see an edit
  // that happens AFTER the load settles.
  const lastSettledRevRef = useRef<number | null>(null);

  // When revisionId changes (load / switch), mark the new revision
  // as just-settled so the very next topology change won't fire a
  // save. The change after that will.
  useEffect(() => {
    lastSettledRevRef.current = revisionId;
    // Clear any pending save from the previous revision — we don't
    // want to overwrite the OLD revision with the NEW revision's
    // freshly-loaded state.
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [revisionId]);

  useEffect(() => {
    // Standalone mode — no project, no API saves.
    if (!projectId || revisionId === null) return;

    // Right after a revision load, the first effect run sees the
    // freshly-loaded state. Skip it.
    if (lastSettledRevRef.current === revisionId) {
      lastSettledRevRef.current = null;
      return;
    }

    // Debounce — reset the timer on every change.
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      const setSaveStatus = useEditorStore.getState().setSaveStatus;
      setSaveStatus('saving');
      updateRevision(revisionId, topology)
        .then(() => setSaveStatus('saved', Date.now()))
        .catch((err) => {
          console.warn('[Revisions] auto-save failed:', err);
          setSaveStatus('error');
        });
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // We deliberately watch topology + nodes + edges so any edit
    // bumps the timer. revisionId / projectId guard the early exits
    // above; the dep array lists them so re-mount happens on switch.
  }, [topology, nodes, edges, projectId, revisionId]);
}
